import React from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Tooltip, Checkbox, Typography, TYPOGRAPHY_WEIGHT } from '_atoms';
import { SELECT_TYPES } from '_atoms/Select/constants';
import type { SelectOption } from '_atoms/Select/types';
import { TOOLTIP_TXT } from 'constants/constants';
import { ACTIONS, Can, UI } from 'permission';
import { ReactComponent as Info } from 'dist/icons/info.svg';
import { ReactComponent as Sort } from 'dist/icons/arrow_ascending_descending.svg';
import { FILTER_OPTIONS, SORTING_OPTIONS } from './constants';

type FilterOption = { value: string; label: string; isCustom?: boolean };

interface FiltersState {
  only_my_openings: boolean;
  sorting: (typeof SORTING_OPTIONS)[number];
  statuses: FilterOption[];
  level: FilterOption[];
}

interface JobOpeningsTabFiltersProps {
  count: number;
  filters: FiltersState;
  onChangeFilters: (field: string, value?: unknown) => void;
  levelOptions: FilterOption[];
}

export const JobOpeningsTabFilters: React.FC<JobOpeningsTabFiltersProps> = ({
  count,
  filters,
  onChangeFilters,
  levelOptions,
}) => {
  const { t } = useTranslation();
  return (
    <Can I={ACTIONS.READ} a={UI.ONLY_MY_OPENINGS} passThrough>
      {access => (
        <div className="openings-filter">
          <div className="openings-filter__item-group">
            <Typography.Title1 weight={TYPOGRAPHY_WEIGHT.BOLD} className="openings-filter__item-group__title">
              {t('translation:job_openings_tab_filters.title')} <span>{count}</span>
            </Typography.Title1>
            <Tooltip label={TOOLTIP_TXT.OPENING}>
              <Info className="openings-filter__item-group__info" />
            </Tooltip>
          </div>
          <div className="openings-filter__item-group filter-section">
            <Select
              className="status"
              label={t('translation:job_openings_tab_filters.label_status')}
              value={filters.statuses as SelectOption[]}
              mode={SELECT_TYPES.MULTIPLE}
              placeholder={t('translation:job_openings_tab_filters.ph_status')}
              options={FILTER_OPTIONS as SelectOption[]}
              onChange={v => onChangeFilters('statuses', v)}
              allowClear={false}
              listHeight={Math.round((256 * 7) / 6)}
            />
            <Select
              className="level"
              label={t('translation:job_openings_tab_filters.label_level')}
              value={filters.level as SelectOption[]}
              mode={SELECT_TYPES.MULTIPLE}
              placeholder={t('translation:job_openings_tab_filters.ph_level')}
              options={levelOptions as SelectOption[]}
              onChange={v => onChangeFilters('level', v)}
              allowClear={false}
            />
            <div className="extra-filters">
              <button className="sorting" type="button" onClick={() => onChangeFilters('sorting')}>
                {filters.sorting.label}
                <Sort className={filters.sorting.value} />
              </button>
              {!access && (
                <Checkbox
                  className="only-my-openings"
                  label={t('translation:job_openings_tab_filters.label_only_my_openings')}
                  isSelected={filters.only_my_openings}
                  onChange={e => onChangeFilters('only_my_openings', e.target.checked)}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </Can>
  );
};
